/** Matches `for-desktop` / `for-mobile` visibility in video testimonial layout SCSS. */
const DESKTOP_SWIPER_MQ = window.matchMedia("(min-width: 1025px)");


function getCardHoverScope(card) {
  return (
    card.closest(".video-testimonial__card-wrapper") ||
    card.closest(".swiper-slide") ||
    card.parentElement
  );
}

/**
 * Set the first card in a scope to be "large", reset others.
 * @param {HTMLElement} scope
 */
function setDefaultLargeInScope(scope) {
  if (!scope) return;
  const cards = scope.querySelectorAll(".video-testimonail__card");
  if (cards.length) {
    cards.forEach(c => c.classList.remove("large"));
    cards[0].classList.add("large");
  }
}

/**
 * Get the card-wrapper container in a slide or return the slide itself.

 */
function getSlideCardScope(slideEl) {
  return slideEl.querySelector(".video-testimonial__card-wrapper") || slideEl;
}

/**
 * Resets all 'large' states for slides in a given slider.

 */
function resetAllSlidesInSlider(sliderEl) {
  if (!sliderEl) return;
  sliderEl.querySelectorAll(".swiper-slide").forEach(slideEl => {
    const scope = getSlideCardScope(slideEl);
    if (scope.querySelector(".video-testimonail__card")) setDefaultLargeInScope(scope);
  });
}

/**
 * Ensures the section has a unique ID, returns it.

 */
function ensureSectionId(sectionEl, sectionIndex) {
  if (!sectionEl.id) sectionEl.id = `video-testimonial-${sectionIndex}`;
  return sectionEl.id;
}

/**
 * Destroys all existing GLightbox instances in the given array.
 * @param {Array} instances
 */
function destroyLightboxInstances(instances) {
  instances.forEach(instance => {
    if (instance?.destroy instanceof Function) instance.destroy();
  });
  instances.length = 0;
}

/**
 * Initializes GLightbox for all video testimonial cards in a section.

 */
function initVideoTestimonialLightboxes(sectionEl, sectionIndex, instancesOut) {
  if (typeof GLightbox === "undefined") return;

  const scopeId = ensureSectionId(sectionEl, sectionIndex);
  const escId = CSS.escape(scopeId);
  const slideScope = ".swiper-slide:not(.swiper-slide-duplicate)";
  const selectors = [
    `#${escId} .js-video-testimonail-swiper.for-desktop ${slideScope} a.glightbox.video-testimonail__card-link`,
    `#${escId} .js-video-testimonail-swiper.for-mobile ${slideScope} a.glightbox.video-testimonail__card-link`,
  ];

  selectors.forEach(selector => {
    if (sectionEl.querySelector(selector)) {
      instancesOut.push(
        GLightbox({
          selector,
          loop: true,
        })
      );
    }
  });
}

/**
 * Binds hover and mouse leave logic to handle "large" card state.
 * @param {HTMLElement} sectionEl
 */
function bindCardLargeHover(sectionEl) {
  const scopes = new Set();
  sectionEl.querySelectorAll(".video-testimonail__card").forEach(card => {
    const scope = getCardHoverScope(card);
    if (scope) scopes.add(scope);
  });

  scopes.forEach(scope => {
    setDefaultLargeInScope(scope);
    scope.addEventListener("mouseleave", () => setDefaultLargeInScope(scope));
  });

  sectionEl.querySelectorAll(".video-testimonail__card").forEach(card => {
    card.addEventListener("mouseenter", () => {
      const scope = getCardHoverScope(card);
      if (scope) {
        scope.querySelectorAll(".video-testimonail__card").forEach(c => c.classList.remove("large"));
        card.classList.add("large");
      }
    });
  });
}

/**
 * Initializes the video testimonial swiper and lightbox logic.
 */
export function initVideoTestimonial() {
  if (typeof Swiper === "undefined") return;

  const sections = document.querySelectorAll(".video-testimonial");
  if (!sections.length) return;

  const syncHandlers = [];

  sections.forEach((sectionEl, sectionIndex) => {
    bindCardLargeHover(sectionEl);
    const lightboxInstances = [];

    const desktopSlider = sectionEl.querySelector(".js-video-testimonail-swiper.for-desktop");
    const mobileSlider = sectionEl.querySelector(".js-video-testimonail-swiper.for-mobile");
    const nav = sectionEl.querySelector(".video-testimonial__swiper-nav");
    const btnPrev = sectionEl.querySelector(".video-testimonial__swiper-nav__prev");
    const btnNext = sectionEl.querySelector(".video-testimonial__swiper-nav__next");

    let swiperInstance = null;

    // Helper: Destroys the Swiper instance if it exists.
    const destroySwiper = () => {
      swiperInstance?.destroy?.(true, true);
      swiperInstance = null;
    };

    // Helper: Returns the active slider element based on viewport.
    const getActiveSliderEl = () => (DESKTOP_SWIPER_MQ.matches ? desktopSlider : mobileSlider);

    /**
     * Handles (re)initialization of Swiper and Lightbox on viewport or content changes.
     */
    const sync = () => {
      destroyLightboxInstances(lightboxInstances);
      destroySwiper();

      const sliderEl = getActiveSliderEl();
      if (!sliderEl) {
        initVideoTestimonialLightboxes(sectionEl, sectionIndex, lightboxInstances);
        return;
      }
      const slides = sliderEl.querySelectorAll(".swiper-slide");
      const hasMultipleSlides = slides.length > 1;

      if (nav) nav.style.display = hasMultipleSlides ? "" : "none";

      if (!hasMultipleSlides) {
        resetAllSlidesInSlider(sliderEl);
        initVideoTestimonialLightboxes(sectionEl, sectionIndex, lightboxInstances);
        return;
      }

      swiperInstance = new Swiper(sliderEl, {
        slidesPerView: 1,
        spaceBetween: 24,
        watchOverflow: true,
        loop: true,
        breakpoints: {
          1024: { slidesPerView: 1 },
          768: { slidesPerView: 2, spaceBetween: 24 },
          480: { slidesPerView: 1, spaceBetween: 16 },
        },
        navigation: btnPrev && btnNext ? { prevEl: btnPrev, nextEl: btnNext } : {},
      });

      swiperInstance.on("slideChangeTransitionEnd", () => resetAllSlidesInSlider(sliderEl));

      resetAllSlidesInSlider(sliderEl);
      initVideoTestimonialLightboxes(sectionEl, sectionIndex, lightboxInstances);
    };

    sync();
    syncHandlers.push(sync);
  });

  /**
   * Handles updating the component when the viewport changes (for responsive Swiper).
   */
  const onViewportChange = () => syncHandlers.forEach(fn => fn());

  if (typeof DESKTOP_SWIPER_MQ.addEventListener === "function") {
    DESKTOP_SWIPER_MQ.addEventListener("change", onViewportChange);
  } else {
    DESKTOP_SWIPER_MQ.addListener(onViewportChange);
  }
}
